import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SeatPersonalization {
  job_role: string | null;
  job_title: string | null;
  preferred_decision_areas: string[];
  team_size: string | null;
  company_phase: string | null;
  decision_style: string[];
  communication_preference: string | null;
  detail_level: string | null;
  current_priorities: string[];
  personalization_completed_at: string | null;
}

export function useSeatPersonalization(workspaceId: string | null, userId: string | null) {
  const [personalization, setPersonalization] = useState<SeatPersonalization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function loadPersonalization() {
      if (!workspaceId || !userId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('workspace_members')
          .select('job_role, job_title, preferred_decision_areas, team_size, company_phase, decision_style, communication_preference, detail_level, current_priorities, personalization_completed_at')
          .eq('workspace_id', workspaceId)
          .eq('user_id', userId)
          .eq('seat_status', 'active')
          .single();

        if (fetchError) throw fetchError;

        setPersonalization({
          job_role: data.job_role,
          job_title: data.job_title,
          preferred_decision_areas: (data.preferred_decision_areas as string[]) || [],
          team_size: data.team_size,
          company_phase: data.company_phase,
          decision_style: (data.decision_style as string[]) || [],
          communication_preference: data.communication_preference,
          detail_level: data.detail_level,
          current_priorities: (data.current_priorities as string[]) || [],
          personalization_completed_at: data.personalization_completed_at,
        });
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    }

    loadPersonalization();
  }, [workspaceId, userId]);

  const savePersonalization = async (data: {
    job_role: string;
    job_title?: string;
    preferred_decision_areas: string[];
    team_size?: string;
    company_phase?: string;
    decision_style?: string[];
    communication_preference?: string;
    detail_level?: string;
    current_priorities?: string[];
  }) => {
    if (!workspaceId || !userId) {
      throw new Error('Workspace ID and User ID are required');
    }

    try {
      const { data: result, error: saveError } = await supabase
        .from('workspace_members')
        .update({
          job_role: data.job_role,
          job_title: data.job_title || null,
          preferred_decision_areas: data.preferred_decision_areas,
          team_size: data.team_size || null,
          company_phase: data.company_phase || null,
          decision_style: data.decision_style || [],
          communication_preference: data.communication_preference || 'automatic',
          detail_level: data.detail_level || 'adaptive',
          current_priorities: data.current_priorities || [],
          personalization_completed_at: new Date().toISOString(),
        })
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .eq('seat_status', 'active')
        .select()
        .single();

      if (saveError) throw saveError;

      setPersonalization({
        job_role: data.job_role,
        job_title: data.job_title || null,
        preferred_decision_areas: data.preferred_decision_areas,
        team_size: data.team_size || null,
        company_phase: data.company_phase || null,
        decision_style: data.decision_style || [],
        communication_preference: data.communication_preference || 'automatic',
        detail_level: data.detail_level || 'adaptive',
        current_priorities: data.current_priorities || [],
        personalization_completed_at: new Date().toISOString(),
      });

      return result;
    } catch (err) {
      throw err;
    }
  };

  return { personalization, loading, error, savePersonalization };
}